import { computed, nextTick, onMounted, onUnmounted, watch, type ComputedRef, type Reactive, type Ref } from 'vue';
import { getGeoItemMeta, syncGeoItemWithParent } from 'src/lib/geoItemHelper';
import { logMoviePlay } from 'src/lib/analyticsHelper';
import { FULL_PLAYER_TYPE } from 'src/consts/movie_player';
import { type GeoItemsMeta } from 'src/models/geoItem';
import {
  getCurrentLocationDisplayInfo,
  isTimeDispChanged,
  preCalculateMovieVals,
} from 'src/lib/moviePlayerHelper';
import type {
  FullscreenPlayerEmit,
  FullscreenPlayerProps,
  MoviePlayerParams,
  MoviePlayerState,
} from 'src/models/moviePlayer';
import type { CustomGeoItemLayer } from 'src/models/user';
import type { IExtremeMapRef, IMovieMetaInfoArea, IMoviePlayerControlArea } from 'src/models';
import type { MastersContext } from 'src/models/master';
import type {
  useMoviePlayerDownloadFlags,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerDownloadFlags';
import type {
  useMoviePlayerSensorChart,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerSensorChart';
import type {
  useMoviePlayerTagModal,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerTagModal';
import type {
  useMoviePlayerMapLinkage,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerMapLinkage';
import type {
  useMoviePlayerMovieList,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerMovieList';
import type {
  useMoviePlayerInfraDoctor,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerInfraDoctor';

// BasicMoviePlayerのuse*Playbackが公開する、
// オーケストレーション層から見た再生系の依存。
export interface OrchestrationPlaybackDeps {
  videoAspectRatio: number;
  findFirstSeekPoint: () => number;
  invalidateCurrentMovie: () => void;
  prepareVideoPlayers: () => Promise<void>;
  resetVideoPlayers: () => Promise<void>;
  videoPlayerHasError: () => boolean;
  seekMovie: (offsetMsec: number) => Promise<void>;
  setPlaySpeed: (val: number) => void;
  playUI: () => void;
  pauseUI: () => void;
  setVideoControlPropertiesToDefault: () => void;
  togglePlayPauseUI: () => void;
  tickMovieBackward: () => Promise<void>;
  tickMovieForward: () => Promise<void>;
  initVjsPreparedState: (vids: string[]) => void;
  getCurrentPlaySpeed: () => number;
}

export interface OrchestrationSubComposables<TPlayback extends OrchestrationPlaybackDeps> {
  download: ReturnType<typeof useMoviePlayerDownloadFlags>;
  playback: TPlayback;
  sensor: ReturnType<typeof useMoviePlayerSensorChart>;
  tagModal: ReturnType<typeof useMoviePlayerTagModal>;
  map: ReturnType<typeof useMoviePlayerMapLinkage>;
  movieList: ReturnType<typeof useMoviePlayerMovieList>;
  infraDoctor: ReturnType<typeof useMoviePlayerInfraDoctor>;
}

export interface OrchestrationPlayerState {
  state: Reactive<MoviePlayerState>;
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  refTopBarArea: Ref<HTMLDivElement | undefined>;
  refVideoArea: Ref<HTMLDivElement | undefined>;
  refControlArea: Ref<IMoviePlayerControlArea | undefined>;
  refMetaInfoArea: Ref<IMovieMetaInfoArea | undefined>;
  masters: Reactive<MastersContext>;
  resolveMastersPromise: () => Promise<void>;
}

export interface OrchestrationConfig {
  // video.js準備完了ポーリング対象vid一覧
  vjsPreparedIds: () => string[];
}

export interface OrchestrationContext {
  compareModeTooltip: ComputedRef<string>;
  switchToCompareMode: () => void;
  closeMoviePlayer: () => void;
  shortcutKeyAction: (key: string) => void;
  onCurrentMovieListIdChangeBySelect: () => void;
}

// BasicMoviePlayerのオーケストレーション（watch/onMounted/画面制御メソッド）の実装。
// 再生系のvid一覧差分はconfigとして注入する。
export function useMoviePlayerOrchestrationCore<TPlayback extends OrchestrationPlaybackDeps>(
  props: FullscreenPlayerProps,
  emit: FullscreenPlayerEmit,
  playerState: OrchestrationPlayerState,
  sub: OrchestrationSubComposables<TPlayback>,
  config: OrchestrationConfig,
): OrchestrationContext {
  const {
    state,
    refExtremeMap,
    refTopBarArea,
    refVideoArea,
    refControlArea,
    refMetaInfoArea,
    masters,
    resolveMastersPromise,
  } = playerState;
  const { playback, sensor, map, movieList } = sub;

  onMounted(async() => {
    // 子のMoviePlayerVideoはonMounted(prepareVideojs)で先にマウントされ、
    // vjs.ready()の完了タイミングは
    // 非同期であるため、await前(同期区間)で先にvjsPrepared状態を初期化しないと、
    // 後続のawaitで実時間が経過する間にvjs.ready()が先に完了し
    // onVjsPreparedでtrueがセットされたのを
    // ここで丸ごとfalseに上書きしてしまい、prepareVideojs()の多重初期化防止ガードにより
    // 二度とpreparedが発火せずprepareVideoPlayers()のポーリングが永久に解決しなくなる。
    playback.initVjsPreparedState(config.vjsPreparedIds());
    await resolveMastersPromise();
    window.addEventListener('resize', onResize);
    await initializeDataManagers();
  });
  onUnmounted(() => {
    window.removeEventListener('resize', onResize);
  });

  // watch:
  watch(() => props.movieLists, () => {
    state.movieList.listSelection.currentMovieLists = props.movieLists.map(ml => ({
      ...ml,
      isSelected: true, // 赤枠
      showTimeTexts: true, // 時間表示
    }));
  });
  watch(() => props.movieListId, () => {
    if (!props.movieListId) return;

    state.movieList.listSelection.currentMovieListId = props.movieListId;
    onCurrentMovieListIdChange();
  });
  watch(() => state.geo.currentMovieCurrentDt, (newTimestamp, oldTimestamp) => {
    // 再生速度が変更できることを考慮し、
    // 時刻表示が変更されたタイミングで場所の表示を更新する
    if (!isTimeDispChanged(newTimestamp, oldTimestamp)) return;

    const currentMovieMgis = state.movieList.current.currentMovie?.movieGeoIndices;
    if (!state.geo.currentMgi || !state.geo.currentMovieCurrentDt || !currentMovieMgis) {
      // mgiが取れない場合は地図上のピンの位置の更新はしない
      state.geo.locationDisp = '';
      return;
    }
    const mgis = [ ...currentMovieMgis ];
    const nextMovieIdx = state.movieList.current.currentMovieIdx + 1;
    const nextMovie = state.movieList.listSelection.currentMovieList?.movies[nextMovieIdx];
    if (nextMovie) {
      mgis.push(...nextMovie.movieGeoIndices);
    }
    const locationDisplayInfo = getCurrentLocationDisplayInfo({
      currentTimestamp: state.geo.currentMovieCurrentDt,
      movieGeoIndices: mgis,
      currentMgi: state.geo.currentMgi,
      currentMgiIndex: state.geo.currentMgiIdx,
    });
    state.geo.locationDisp = locationDisplayInfo.movieMetaInfoLocationDisp;
    map.updateMapPinPosition({
      lat: locationDisplayInfo.location.lat.toString(),
      lon: locationDisplayInfo.location.lon.toString(),
    });
  });

  // computed
  const compareModeTooltip = computed(() => {
    let ret = '';
    if (movieList.isCurrentMovieListSearchTypeRoute) {
      ret = 'ルート検索の場合、比較モードは利用できません';
    }
    return ret;
  });

  // methods
  const initializeDataManagers = async() => {
    const layerListDefault: CustomGeoItemLayer[] = [
      { name: 'pin', dispName: 'pin', hideToggleCheckbox: false, isCreatable: false, isTabPanel: false, tabLabel: '' },
      {
        name: 'comment',
        dispName: '付箋',
        hideToggleCheckbox: false,
        isCreatable: false,
        isTabPanel: false,
        tabLabel: '',
      },
    ];
    state.geo.geoItemsMeta.map = await getGeoItemMeta(layerListDefault);
  };
  const startPlayingMovie = async() => {
    onResize();
    playFromBeginning(playback.findFirstSeekPoint());
    // currentMovieは再生末尾時点のmgiを持っておらず、
    // そのままだと地図上には50秒時点あたりの経路までしか描画できない.
    // そこで事前に続きの動画があれば取得し、mgiを補足する.
    await movieList.appendContinuedMovieMgiToCurrentMovieList();
    map.updateMap();
  };
  const onCurrentMovieListIdChange = async() => {
    for (const ml of Object.values(state.movieList.listSelection.currentMovieLists)) {
      if (ml.id === state.movieList.listSelection.currentMovieListId) {
        state.movieList.listSelection.currentMovieList = ml;
        playback.invalidateCurrentMovie();
        break;
      }
    }
    if (!state.movieList.listSelection.currentMovieList) return;

    preCalculateMovieVals(state.movieList.listSelection.currentMovieList, masters.kp.roadNameDispMap);
    movieList.setMoviePlayerControlDispVals(state.movieList.listSelection.currentMovieList);
    if (!state.isInitialized) {
      // 画面が見えてる状態でvideojsの初期化を行わないとvueの処理との兼ね合いで
      // DOMが変なところに挿入されたりする現象があったので、見えていることが
      // 分かっている状態からnextTickしてからinitialize
      nextTick(async() => {
        await initializeViews();
        await startPlayingMovie();
      });
    } else {
      await startPlayingMovie();
    }
    if (refExtremeMap.value) {
      syncGeoItemWithParent(
        // reactive()のUnwrapNestedRefsで型が変換されているため、元のGeoItemsMeta型へ戻す
        state.geo.geoItemsMeta as GeoItemsMeta,
        props.parentGeoItemsMeta,
        refExtremeMap.value,
      );
    }
  };
  const onCurrentMovieListIdChangeBySelect = () => {
    state.movieList.listSelection.showMovieListSelect = false;
    onCurrentMovieListIdChange();
  };
  const initializeViews = () => {
    if (state.isInitialized) return Promise.resolve();

    state.isInitialized = true;
    const p1 = playback.prepareVideoPlayers();
    const p2 = map.prepareMap();
    return Promise.all([p1, p2]);
  };
  const closeMoviePlayer = () => {
    setAllUIStateToDefault();
    // 裏でダウンロードさせないため、reset
    nextTick(() => playback.resetVideoPlayers());
    // 続き動画をreset
    state.movieList.current.continuedMovies = [];
    emit('close');
  };
  const setAllUIStateToDefault = async() => {
    playback.pauseUI();
    state.movieList.listSelection.showMovieListSelect = false;
    state.geo.mapElemInfo = null;
    playback.setVideoControlPropertiesToDefault();
    if (refControlArea.value) {
      refControlArea.value.resetVolume();
    }
    if (refMetaInfoArea.value) {
      refMetaInfoArea.value.setAllUIStateToDefault();
    }
    sensor.setSensorChartUIStateToDefault();
  };
  const playFromBeginning = async(seekPoint: number) => {
    if (playback.videoPlayerHasError()) {
      await playback.resetVideoPlayers();
    }
    const playSpeed = playback.getCurrentPlaySpeed();
    await playback.seekMovie(seekPoint);
    if (state.geo.currentMgi) {
      map.updateMapPinPosition({
        lat: state.geo.currentMgi.location.lat,
        lon: state.geo.currentMgi.location.lon,
      });
    }
    await setAllUIStateToDefault();
    playback.setPlaySpeed(playSpeed);
    playback.playUI();
    logMoviePlay(FULL_PLAYER_TYPE);
  };
  const shortcutKeyAction = (key: string) => {
    switch (key) {
      case 'space':
        playback.togglePlayPauseUI();
        break;
      case 'left':
        playback.tickMovieBackward();
        break;
      case 'right':
        playback.tickMovieForward();
        break;
      case 'escape':
        if (state.ui.movieTagModal.show) {
          state.ui.movieTagModal.show = false;
        }
        break;
    }
  };
  const switchToCompareMode = () => {
    setAllUIStateToDefault();
    // 同一路線#方向でしぼる
    const mlCurrent = state.movieList.listSelection.currentMovieList;
    const refRoadNameDisp = mlCurrent?.startKp.roadNameDisp;
    const refDirection = mlCurrent?.startKp.direction;
    const filteredMovieLists = props.movieLists.filter(ml => {
      return refRoadNameDisp === ml.startKp.roadNameDisp &&
        refDirection === ml.startKp.direction;
    });
    const params: MoviePlayerParams = {
      movieLists: filteredMovieLists,
      movieListId: state.movieList.listSelection.currentMovieListId,
    };
    emit('switch-to-compare-mode', params);
  };
  const onResize = () => {
    if (!movieList.isCurrentMovieListReady) return;

    const windowW = window.innerWidth;
    const windowH = window.innerHeight;
    // モーダルのmarginおよびpadding, video間のmargin
    const wMargins = (8 * 2 + 8 * 2) + (2 * 6);
    const hMargins = 6 * 2 + 8 * 2;
    const videoH = (windowW - wMargins) / 3 / playback.videoAspectRatio;
    state.ui.styles.videoHeight = parseInt(videoH.toString()) + 'px';
    nextTick(() => {
      if (!refTopBarArea.value || !refVideoArea.value) return;

      const topBarH = refTopBarArea.value.clientHeight;
      const videoAreaH = refVideoArea.value.clientHeight;
      const otherHMargins = 16 + 8;
      const infoH = windowH - hMargins - topBarH - videoAreaH - otherHMargins;
      state.ui.styles.infoHeight = infoH + 'px';
      resizeMap();
    });
  };
  const resizeMap = () => {
    const mapHeight = parseInt(state.ui.styles.infoHeight);
    if (!refExtremeMap.value) return;

    refExtremeMap.value.setMapHeight(mapHeight);
    refExtremeMap.value.triggerResize();
  };

  return {
    compareModeTooltip,
    switchToCompareMode,
    closeMoviePlayer,
    shortcutKeyAction,
    onCurrentMovieListIdChangeBySelect,
  };
}
